/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import { Star, User, CheckCircle } from 'lucide-react';
import { useVendorReviews } from '../../services/vendorService';

const VendorReviews = ({ vendorId }) => {
  const { data: reviewsResponse, isLoading, error } = useVendorReviews(vendorId, {
    per_page: 10,
  });

  const paginatedData = reviewsResponse?.data;
  const reviews = Array.isArray(paginatedData?.data)
    ? paginatedData.data
    : Array.isArray(paginatedData)
      ? paginatedData
      : [];
  const totalReviews =
    paginatedData?.total ?? (Array.isArray(paginatedData) ? paginatedData.length : 0);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-32 animate-pulse rounded bg-m-border-light" />
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="animate-pulse rounded-2xl border border-m-border-light bg-m-surface p-4"
          >
            <div className="mb-2 h-3 w-20 rounded bg-m-border-light" />
            <div className="mb-1 h-3 w-full rounded bg-m-border-light" />
            <div className="h-3 w-3/4 rounded bg-m-border-light" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-m-border-light bg-m-surface py-8 text-center">
        <p className="text-sm text-red-600">
          Failed to load reviews. Please try again later.
        </p>
      </div>
    );
  }

  if (totalReviews === 0) {
    return (
      <div className="rounded-2xl border border-m-border-light bg-m-surface py-10 text-center sm:py-12">
        <Star className="mx-auto mb-3 h-10 w-10 text-m-ink-faint sm:mb-4 sm:h-12 sm:w-12" />
        <h3 className="mb-1 text-base font-medium tracking-tight text-m-ink sm:mb-2 sm:text-lg">
          No reviews yet
        </h3>
        <p className="text-sm text-m-ink-muted">
          Be the first to review this vendor after your booking.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="mb-2 flex items-center justify-between sm:mb-3">
        <h3 className="text-base font-medium tracking-tight text-m-ink sm:text-lg">
          Customer reviews
        </h3>
        <span className="text-xs text-m-ink-muted sm:text-sm">
          {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
        </span>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="border-b border-m-border-light pb-3 last:border-0 last:pb-0 sm:pb-4"
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="shrink-0">
                {review.client?.profile_image_url ? (
                  <img
                    src={review.client.profile_image_url}
                    alt={review.client.name || 'User'}
                    className="h-8 w-8 rounded-full object-cover sm:h-10 sm:w-10"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-m-border-light sm:h-10 sm:w-10">
                    <User className="h-4 w-4 text-m-ink-faint sm:h-5 sm:w-5" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-1.5 sm:mb-1.5 sm:gap-2">
                  <h4 className="text-xs font-medium text-m-ink sm:text-sm">
                    {review.client?.name || 'Anonymous'}
                  </h4>
                  {review.is_verified ? (
                    <CheckCircle className="h-3.5 w-3.5 shrink-0 text-m-ink sm:h-4 sm:w-4" />
                  ) : null}
                </div>

                <div className="mb-1.5 flex items-center gap-2 sm:mb-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${
                          star <= review.rating
                            ? 'fill-current text-m-ink'
                            : 'text-m-border-light'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-m-ink-muted">
                    {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                {review.title ? (
                  <h5 className="mb-0.5 text-xs font-medium text-m-ink sm:mb-1 sm:text-sm">
                    {review.title}
                  </h5>
                ) : null}

                {review.comment ? (
                  <p className="text-xs leading-relaxed text-m-ink-muted sm:text-sm">
                    {review.comment}
                  </p>
                ) : null}

                {review.images && review.images.length > 0 ? (
                  <div className="mt-2 grid grid-cols-3 gap-1.5 sm:mt-3 sm:gap-2">
                    {review.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Review image ${index + 1}`}
                        className="h-16 w-full rounded-lg object-cover sm:h-20"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorReviews;
